import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import axios from "axios";
import { toast } from "sonner";
import { 
  Settings as SettingsIcon, 
  Bot, 
  RotateCcw,
  Save,
  Loader2,
  Sparkles,
  Info,
  Server,
  Play,
  Square,
  Download,
  CheckCircle,
  XCircle,
  AlertCircle,
  Bug,
  Copy,
  FileText,
  Ban,
  Trash2
} from "lucide-react";

const DEFAULT_PROMPT = `Você é um assistente especializado em análise de ocupação de gôndolas e refrigeradores de varejo.

Quando receber uma imagem, você deve:
1. Identificar o tipo de móvel/equipamento (geladeira, freezer, gôndola, etc.)
2. Analisar a ocupação das prateleiras
3. Classificar como: BOA (>=80% ocupado), RUIM (<80%), VAZIA (1-2 prateleiras vazias), ou CRÍTICA (3+ prateleiras vazias)
4. Fornecer detalhes sobre o que foi observado

Seja objetivo e direto nas respostas. Use emojis para facilitar a leitura.`;

export default function Settings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    ai_prompt: DEFAULT_PROMPT,
    response_template: "",
    auto_reply_enabled: true
  });
  
  // WhatsApp Service State
  const [serviceStatus, setServiceStatus] = useState(null);
  const [loadingService, setLoadingService] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [installLogs, setInstallLogs] = useState([]);
  
  // Diagnostics State
  const [diagnostics, setDiagnostics] = useState(null);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  
  // Blacklist State
  const [blacklist, setBlacklist] = useState([]);
  const [loadingBlacklist, setLoadingBlacklist] = useState(false);
  const [availableMoveis, setAvailableMoveis] = useState([]);

  useEffect(() => {
    loadSettings();
    loadServiceStatus();
    loadBlacklist();
    loadMoveis();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/ai`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error("Error loading settings:", error);
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  };

  const loadBlacklist = async () => {
    setLoadingBlacklist(true);
    try {
      const response = await axios.get(`${API}/settings/blacklist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlacklist(response.data.moveis || []);
    } catch (error) {
      console.error("Error loading blacklist:", error);
    } finally {
      setLoadingBlacklist(false);
    }
  };

  const loadMoveis = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/gabaritos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableMoveis(response.data.moveis || []);
    } catch (error) {
      console.error("Error loading moveis:", error);
    }
  };

  const removeFromBlacklist = async (movelName) => {
    try {
      await axios.post(`${API}/settings/blacklist/remove`, {
        movel_name: movelName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Móvel "${movelName}" desbloqueado`);
      loadBlacklist();
    } catch (error) {
      toast.error("Erro ao desbloquear móvel");
    }
  };

  const loadServiceStatus = async () => {
    try {
      const response = await axios.get(`${API}/system/whatsapp-service/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Só atualiza se os dados mudaram para evitar flickering
      setServiceStatus(prev => {
        if (JSON.stringify(prev) === JSON.stringify(response.data)) return prev;
        return response.data;
      });
    } catch (error) {
      console.error("Error loading service status:", error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings/ai`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configurações salvas com sucesso!");
    } catch (error) {
      toast.error("Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = async () => {
    if (!window.confirm("Tem certeza que deseja restaurar as configurações padrão?")) {
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/settings/ai/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        ai_prompt: DEFAULT_PROMPT,
        response_template: "",
        auto_reply_enabled: true
      });
      toast.success("Configurações restauradas!");
    } catch (error) {
      toast.error("Erro ao restaurar configurações");
    } finally {
      setSaving(false);
    }
  };

  const installService = async () => {
    setInstalling(true);
    setInstallLogs([]);
    try {
      const response = await axios.post(`${API}/system/whatsapp-service/install`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstallLogs(response.data.logs || []);
      if (response.data.success) {
        toast.success("Instalação concluída!");
        loadServiceStatus();
      } else {
        toast.error("Instalação com erros");
      }
    } catch (error) {
      toast.error("Erro na instalação");
      setInstallLogs(["❌ Erro ao conectar com o servidor"]);
    } finally {
      setInstalling(false);
    }
  };

  const startService = async () => {
    setLoadingService(true);
    try {
      const response = await axios.post(`${API}/system/whatsapp-service/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message);
        setTimeout(loadServiceStatus, 2000);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Erro ao iniciar serviço");
    } finally {
      setLoadingService(false);
    }
  };

  const stopService = async () => {
    setLoadingService(true);
    try {
      const response = await axios.post(`${API}/system/whatsapp-service/stop`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message);
        setTimeout(loadServiceStatus, 2000);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Erro ao parar serviço");
    } finally {
      setLoadingService(false);
    }
  };

  const loadDiagnostics = async () => {
    setLoadingDiagnostics(true);
    try {
      const response = await axios.get(`${API}/system/diagnostics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiagnostics(response.data);
      setShowDiagnostics(true);
    } catch (error) {
      toast.error("Erro ao carregar diagnósticos");
      setDiagnostics({ error: error.message });
      setShowDiagnostics(true);
    } finally {
      setLoadingDiagnostics(false);
    }
  };

  const copyDiagnostics = () => {
    const text = JSON.stringify(diagnostics, null, 2);
    navigator.clipboard.writeText(text);
    toast.success("Diagnóstico copiado para a área de transferência!");
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-6 lg:p-8" data-testid="settings-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-chivo font-bold text-3xl tracking-tight mb-1">Configurações</h1>
            <p className="text-zinc-500 text-sm">Configure o comportamento da IA</p>
          </div>
        </div>

        <div className="max-w-3xl space-y-6">
          {/* AI Prompt Card */}
          <div className="bg-white border border-zinc-200 p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-violet-100 rounded">
                <Bot className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h2 className="font-chivo font-bold text-lg">Prompt da IA</h2>
                <p className="text-sm text-zinc-500">
                  Personalize como a IA deve se comportar ao analisar imagens
                </p>
              </div>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-100 mb-4">
              <div className="flex gap-2">
                <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-700">
                  Este prompt será usado como base para todas as análises de imagem. 
                  A IA combinará este contexto com suas regras internas de classificação.
                </p>
              </div>
            </div>

            <Textarea
              value={settings.ai_prompt}
              onChange={(e) => setSettings({ ...settings, ai_prompt: e.target.value })}
              placeholder="Descreva como a IA deve se comportar..."
              className="min-h-[200px] rounded-none font-mono text-sm"
              data-testid="ai-prompt-input"
            />

            <p className="text-xs text-zinc-400 mt-2">
              {settings.ai_prompt.length} caracteres
            </p>
          </div>

          {/* Response Template Card */}
          <div className="bg-white border border-zinc-200 p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-emerald-100 rounded">
                <Sparkles className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="font-chivo font-bold text-lg">Template de Resposta (Opcional)</h2>
                <p className="text-sm text-zinc-500">
                  Personalize o formato da resposta enviada pelo WhatsApp
                </p>
              </div>
            </div>

            <Textarea
              value={settings.response_template}
              onChange={(e) => setSettings({ ...settings, response_template: e.target.value })}
              placeholder="Ex: 📊 *Análise da {loja}*&#10;Móvel: {movel}&#10;Status: {classificacao}&#10;..."
              className="min-h-[120px] rounded-none font-mono text-sm"
              data-testid="response-template-input"
            />

            <p className="text-xs text-zinc-400 mt-2">
              Variáveis disponíveis: {"{loja}"}, {"{movel}"}, {"{classificacao}"}, {"{ocupacao}"}, {"{detalhes}"}
            </p>
          </div>

          {/* Auto Reply Toggle */}
          <div className="bg-white border border-zinc-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded">
                  <SettingsIcon className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="font-chivo font-bold text-lg">Resposta Automática</h2>
                  <p className="text-sm text-zinc-500">
                    Responder automaticamente quando receber uma imagem no WhatsApp
                  </p>
                </div>
              </div>
              
              <Switch
                checked={settings.auto_reply_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, auto_reply_enabled: checked })}
                data-testid="auto-reply-switch"
              />
            </div>
          </div>

          {/* WhatsApp Service Installation */}
          <div className="bg-white border border-zinc-200 p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded">
                <Server className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h2 className="font-chivo font-bold text-lg">Serviço WhatsApp (Node.js)</h2>
                <p className="text-sm text-zinc-500">
                  Instale e gerencie o serviço de integração com WhatsApp
                </p>
              </div>
            </div>

            {/* Status do Serviço */}
            {serviceStatus && (
              <div className="bg-zinc-50 border border-zinc-200 p-4 mb-4">
                <h3 className="text-sm font-medium mb-3">Status do Sistema</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    {serviceStatus.node_installed ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500" />
                    )}
                    <span>Node.js {serviceStatus.node_version || 'não instalado'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {serviceStatus.npm_installed ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500" />
                    )}
                    <span>npm {serviceStatus.npm_version || 'não instalado'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {serviceStatus.dependencies_installed ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-yellow-500" />
                    )}
                    <span>Dependências {serviceStatus.dependencies_installed ? 'instaladas' : 'pendentes'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {serviceStatus.service_running ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500" />
                    )}
                    <span>Serviço {serviceStatus.service_running ? 'rodando' : 'parado'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Alerta quando Node.js não está instalado */}
            {serviceStatus && !serviceStatus.node_installed && (
              <div className="bg-amber-50 border border-amber-200 p-4 mb-4">
                <div className="flex gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Node.js não está instalado</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Clique em "Instalar Node.js" para baixar e configurar automaticamente.
                      Isso pode levar alguns minutos.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Logs de Instalação */}
            {installLogs.length > 0 && (
              <div className="bg-zinc-900 text-zinc-100 p-4 mb-4 font-mono text-xs max-h-40 overflow-auto">
                {installLogs.map((log, i) => (
                  <div key={i} className="py-0.5">{log}</div>
                ))}
              </div>
            )}

            {/* Botões de Ação */}
            <div className="flex gap-3">
              <Button
                onClick={installService}
                disabled={installing || loadingService}
                variant="outline"
                className="rounded-none gap-2"
                data-testid="install-service-btn"
              >
                {installing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {installing ? 'Instalando...' : 'Instalar Node.js'}
              </Button>
              
              {serviceStatus?.dependencies_installed && !serviceStatus?.service_running && (
                <Button
                  onClick={startService}
                  disabled={loadingService}
                  className="rounded-none gap-2 bg-green-600 hover:bg-green-700 text-white"
                  data-testid="start-service-btn"
                >
                  {loadingService ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  Iniciar Serviço
                </Button>
              )}
              
              {serviceStatus?.service_running && (
                <Button
                  onClick={stopService}
                  disabled={loadingService}
                  variant="outline"
                  className="rounded-none gap-2 border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="stop-service-btn"
                >
                  {loadingService ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  Parar Serviço
                </Button>
              )}
              
              <Button
                onClick={loadServiceStatus}
                variant="ghost"
                className="rounded-none gap-2"
                data-testid="refresh-status-btn"
              >
                <RotateCcw className="w-4 h-4" />
                Atualizar Status
              </Button>
            </div>
          </div>

          {/* Diagnostics Section */}
          <div className="bg-white border border-zinc-200 p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded">
                <Bug className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h2 className="font-chivo font-bold text-lg">Diagnóstico do Sistema</h2>
                <p className="text-sm text-zinc-500">
                  Gere um relatório completo para debug de problemas
                </p>
              </div>
            </div>

            <div className="flex gap-3 mb-4">
              <Button
                onClick={loadDiagnostics}
                disabled={loadingDiagnostics}
                variant="outline"
                className="rounded-none gap-2"
                data-testid="load-diagnostics-btn"
              >
                {loadingDiagnostics ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                Gerar Diagnóstico
              </Button>
              
              {diagnostics && (
                <Button
                  onClick={copyDiagnostics}
                  variant="outline"
                  className="rounded-none gap-2"
                  data-testid="copy-diagnostics-btn"
                >
                  <Copy className="w-4 h-4" />
                  Copiar para Área de Transferência
                </Button>
              )}
            </div>

            {showDiagnostics && diagnostics && (
              <div className="bg-zinc-900 text-zinc-100 p-4 font-mono text-xs max-h-96 overflow-auto">
                <pre className="whitespace-pre-wrap break-all">
                  {JSON.stringify(diagnostics, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* Blacklist Section */}
          <div className="bg-white border border-zinc-200 p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded">
                <Ban className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h2 className="font-chivo font-bold text-lg">Móveis Bloqueados</h2>
                <p className="text-sm text-zinc-500">
                  Móveis que não serão analisados automaticamente
                </p>
              </div>
            </div>

            {loadingBlacklist ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
              </div>
            ) : blacklist.length === 0 ? (
              <div className="bg-zinc-50 border border-zinc-200 p-4 text-center">
                <p className="text-sm text-zinc-500">Nenhum móvel bloqueado</p>
                <p className="text-xs text-zinc-400 mt-1">
                  Você pode bloquear móveis na página de detalhes de uma análise
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {blacklist.map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-200"
                  >
                    <div>
                      <p className="font-medium text-sm">{item.movel_name}</p>
                      {item.reason && (
                        <p className="text-xs text-zinc-500">{item.reason}</p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFromBlacklist(item.movel_name)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={saveSettings}
              disabled={saving}
              className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white gap-2 px-6"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Salvar Configurações
            </Button>
            
            <Button
              onClick={resetSettings}
              disabled={saving}
              variant="outline"
              className="rounded-none gap-2"
              data-testid="reset-settings-btn"
            >
              <RotateCcw className="w-4 h-4" />
              Restaurar Padrão
            </Button>
          </div>
        </div>
      </div>
    </Sidebar>
  );
}
